import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../../services/APIs/zulipServices";

const apiBaseUrl = import.meta.env.VITE_CITYCOLLAB_API_ROOT_URL;

function classNames(...c) {
  return c.filter(Boolean).join(" ");
}

function Avatar({ user, size = "8" }) {
  const initials = useMemo(() => {
    if (!user?.full_name) {
      return "";
    }
    return user.full_name
      .split(" ")
      .map(n => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [user?.full_name]);

  const isValidURL = (str: string | URL) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const avatarImgURL =
    user?.avatar_url && isValidURL(user.avatar_url)
      ? user.avatar_url
      : user?.avatar_url
        ? `${apiBaseUrl}${user.avatar_url}`
        : null;

  return avatarImgURL ? (
    <div className="flex -space-x-2 overflow-hidden">
      <img
        src={avatarImgURL}
        alt="User Avatar"
        className={`inline-block rounded-full object-cover w-${size} h-${size}`}
      />
    </div>
  ) : (
    <div
      className={classNames(
        "flex items-center justify-center rounded text-xs font-medium text-white",
        `w-${size} h-${size}`,
        user?.color ?? "bg-slate-500"
      )}
    >
      {initials}
    </div>
  );
}

const formatMessageDate = (timestamp: any) => {
  const messageDate = new Date(timestamp * 1000);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();

  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();

  if (isToday) {
    return "Today";
  }
  if (isYesterday) {
    return "Yesterday";
  }

  return messageDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatMessageTime = (timestamp: any) =>
  new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const htmlToText = (html: any) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return (tempDiv.textContent ?? tempDiv.innerText) || "";
};

function Composer({
  dmId,
  channelId,
  isDM,
  refetchDMChatData,
  refetchChannelChatData,
}: any) {
  const [text, setText] = useState("");

  const handleSendClickChannel = async e => {
    e.preventDefault(); // prevent form reload
    if (!text.trim()) {
      return;
    } // skip empty messages
    try {
      const payload = {
        to: [Number(channelId)],
        type: "stream",
        topic: "channel events",
        content: text,
      };
      await ZulipServices.sendChannelMessage(payload);
      refetchChannelChatData?.();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setText("");
    }
  };

  const handleSendClickDM = async e => {
    e.preventDefault(); // prevent form reload
    if (!text.trim()) {
      return;
    } // skip empty messages
    try {
      const payload = { to: [Number(dmId)], type: "private", content: text };
      await ZulipServices.sendDirectMessage(payload);
      refetchDMChatData?.();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setText("");
    }
  };

  return (
    <div className="border-slate-200 sticky bottom-0 flex items-center gap-2 border-t p-3">
      <input
        className="flex-1 rounded-lg border border-[#EDEDFD] px-3 py-2 font-sans text-sm focus:ring-2 focus:ring-indigo-600"
        placeholder="Type a message"
        value={text}
        onChange={e => setText(e.target.value)}
      />
      <button
        type="button"
        onClick={isDM ? handleSendClickDM : handleSendClickChannel}
        className="rounded-lg bg-[#635DE8] px-4 py-2 font-sans text-sm text-white transition hover:bg-indigo-700"
      >
        Send
      </button>
    </div>
  );
}

const getDMUserList = (DMUserList: any[], currentUserId: number) => {
  // Filter only direct/private messages
  const privateMessages = DMUserList?.filter(msg => msg.type === "private");

  // Create a unique map of DM users
  const dmUserMap = new Map();

  privateMessages.forEach(msg => {
    // Each private message has an array of 2 participants
    const participants = msg.display_recipient;

    // Find the "other" user (not the current user)
    const otherUser = participants.find(user => user.id !== currentUserId);

    if (otherUser && !dmUserMap.has(otherUser.id)) {
      dmUserMap.set(otherUser.id, {
        id: otherUser.id,
        full_name: otherUser.full_name,
        email: otherUser.email,
        avatar_url: msg.avatar_url?.startsWith("http")
          ? msg.avatar_url
          : `${apiBaseUrl}${msg.avatar_url}`,
        last_message: msg.content.replace(/<[^>]+>/g, ""), // strip HTML tags
        timestamp: msg.timestamp,
      });
    }
  });

  // Convert to array and sort by latest message
  const dmUserList = Array.from(dmUserMap.values()).sort(
    (a, b) => b.timestamp - a.timestamp
  );

  return dmUserList;
};

export function UnreadPanel({ allMessageData }: { allMessageData: any[] }) {
  const [unReadChannelList, setUnReadChannelList] = useState<
    Record<string, any[]>
  >({});
  const [unReadDMUserList, setUnReadDMUserList] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!allMessageData?.length) {
      return;
    }

    const senderCount: Record<string, number> = {};
    allMessageData.forEach(msg => {
      senderCount[msg.sender_email] = (senderCount[msg.sender_email] || 0) + 1;
    });
    const currentUserEmail =
      Object.entries(senderCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "";

    const unreadMessages = allMessageData.filter(
      msg => !msg.flags?.includes("read")
    );
    const unreadChannels = unreadMessages.filter(msg => msg.type === "stream");
    const unreadDMs = unreadMessages.filter(msg => msg.type === "private");

    const groupedChannels: Record<
      string,
      { name: string; id: number; messages: any[] }
    > = {};
    unreadChannels.forEach(msg => {
      const channelName =
        msg.display_recipient ?? msg.stream_name ?? "Unknown Channel";
      const channelId = msg.stream_id ?? msg.recipient_id;
      const key = `${channelName}_${channelId}`;

      if (!groupedChannels[key]) {
        groupedChannels[key] = {
          name: channelName,
          id: channelId,
          messages: [],
        };
      }
      groupedChannels[key].messages.push(msg);
    });

    const groupedDMs: Record<
      string,
      { name: string; id: number; messages: any[] }
    > = {};
    unreadDMs.forEach(msg => {
      const dmId = msg.sender_id;
      let dmName = "";

      if (Array.isArray(msg.display_recipient)) {
        const others = msg.display_recipient.filter(
          u => u.email !== currentUserEmail
        );
        dmName = others.map(u => u.full_name).join(", ");
      } else {
        dmName = msg.display_recipient;
      }

      if (!groupedDMs[dmId]) {
        groupedDMs[dmId] = { name: dmName, id: dmId, messages: [] };
      }
      groupedDMs[dmId].messages.push(msg);
    });

    setUnReadChannelList(groupedChannels);
    setUnReadDMUserList(Object.values(groupedDMs));
  }, [allMessageData]);

  const handleDMClick = (dmId: number) => {
    navigate(`/mm/chat?dmId=${dmId}&isDM=true`);
  };

  const handleChannelClick = (channelId: number) => {
    navigate(`/mm/chat?channelId=${channelId}&isDM=false`);
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      {Object.keys(unReadChannelList).length > 0 && (
        <>
          <h2 className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Channels
          </h2>
          <div className="space-y-1">
            {Object.values(unReadChannelList).map(ch => (
              <div
                className="flex items-center justify-between rounded-2xl border border-[#EDEDFD] bg-white p-4 shadow-[0_2px_16px_0_rgba(10,10,105,0.10)]"
                key={`${ch.id ?? ch.name}`}
                onClick={() => handleChannelClick(ch.id)}
              >
                {/* Left Section - Image */}
                <div className="flex items-center gap-3">
                  <img
                    src="https://picsum.photos/200/300"
                    alt="event"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <h2 className="font-poppins text-[16px] font-semibold text-[#2A2942]">
                      {ch.name}
                    </h2>
                  </div>
                </div>

                {/* Right Section - Notification */}
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#635DE8] text-[13px] font-semibold text-white">
                  {ch.messages.length}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {unReadDMUserList.length > 0 && (
        <>
          <h2 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Direct Messages
          </h2>
          {unReadDMUserList.map((dm: any) => (
            <div
              className="flex items-center justify-between rounded-2xl border border-[#EDEDFD] bg-white p-4 shadow-[0_2px_16px_0_rgba(10,10,105,0.10)]"
              key={`${dm.id ?? dm.name}`}
              onClick={() => handleDMClick(dm.id)}
            >
              {/* Left Section - Image */}
              <div className="flex items-center gap-3">
                <img
                  src="https://picsum.photos/seed/picsum/200/300"
                  alt="event"
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <h2 className="font-poppins text-[16px] font-semibold text-[#2A2942]">
                    {dm.name}
                  </h2>
                </div>
              </div>

              {/* Right Section - Notification */}
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#635DE8] text-[13px] font-semibold text-white">
                {dm.messages.length}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const ChatPage = () => {
  const scrollerRef = useRef(null);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const channelId = queryParams.get("channelId");
  const dmId = queryParams.get("dmId");
  const isDM = queryParams.get("isDM") === "true";

  const [showRight, setShowRight] = useState(true);
  const [channelList, setChannelList] = useState([]);
  const [channelUserList, setChannelUserList] = useState([]);
  const [DMUserList, setDMUserList] = useState([]);
  const [messages, setMessages] = useState([]);
  // const [allMessageData, setAllMessageData] = useState([]);

  const { data: usersData } = useQuery({
    queryKey: ["/users"],
    queryFn: () => ZulipServices.getUsers(),
  });

  const { data: usersDataByStreams } = useQuery({
    queryKey: ["/streams", channelId],
    queryFn: () => ZulipServices.getUsersByChannel(channelId ?? ""),
    enabled: !!channelId,
  });

  const { data: channelsData } = useQuery({
    queryKey: ["/streams"],
    queryFn: () => ZulipServices.getChannelList(),
  });

  useEffect(() => {
    setChannelList(channelsData?.streams ?? []);

    const subscriberIds: number[] = usersDataByStreams?.subscribers ?? [];

    const memberChannelWise =
      usersData?.members?.filter(m => subscriberIds.includes(m.user_id)) ?? [];

    setChannelUserList(memberChannelWise);
  }, [channelsData, usersDataByStreams, usersData]);

  // GET Specific DM USER Chat LIST
  const payload = {
    anchor: 1224,
    num_before: 60,
    num_after: 150,
    client_gravatar: true,
    allow_empty_topic_name: true,
    narrow: JSON.stringify([
      { negated: false, operator: "dm", operand: [Number(dmId)] },
    ]),
  };

  const { data: DMChatData, refetch: refetchDMChatData } = useQuery({
    queryKey: ["messages", payload],
    queryFn: () => ZulipServices.getDirectMessage({ payload }),
    enabled: !!dmId,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (DMChatData?.messages) {
      setMessages(DMChatData?.messages);
    }
  }, [DMChatData]);

  // GET DM USER LIST
  const DMUserListPayload = {
    anchor: "newest",
    num_before: 1000,
    num_after: 0,
    client_gravatar: true,
    allow_empty_topic_name: true,
  };

  const { data: DMUserData } = useQuery({
    queryKey: ["messages", DMUserListPayload],
    queryFn: () => ZulipServices.getDMUserList({ DMUserListPayload }),
  });

  useEffect(() => {
    if (DMUserData?.messages) {
      // setAllMessageData(DMUserData?.messages);
      const data = getDMUserList(DMUserData?.messages, 51);

      setDMUserList(data);
    }
  }, [DMUserData]);

  // GET Chat of Channel
  const chatOfChannelPayload = {
    anchor: "newest",
    num_before: 50,
    num_after: 0,
    narrow: JSON.stringify([
      { negated: false, operator: "channel", operand: Number(channelId) },
    ]),
    client_gravatar: true,
    allow_empty_topic_name: true,
  };

  const { data: channelChatData, refetch: refetchChannelChatData } = useQuery({
    queryKey: ["messages", chatOfChannelPayload],
    queryFn: () => ZulipServices.getChatOfChannel({ chatOfChannelPayload }),
    enabled: !!channelId,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (channelChatData?.messages) {
      setMessages(channelChatData?.messages);
    }
  }, [channelChatData]);

  const activeLabel = useMemo(() => {
    if (channelId && !isDM) {
      const c = channelList?.find(x => x.stream_id === Number(channelId));
      return c ? `#${c.name}` : "-";
    }
    if (dmId && isDM) {
      const d = DMUserList?.find(x => x.id === Number(dmId));
      return d ? d.full_name : "-";
    }
    return "Conversation";
  }, [channelList, DMUserList, channelId, dmId, isDM]);

  const groupedMessages = useMemo(() => {
    return messages?.reduce((groups, msg) => {
      const dateLabel = formatMessageDate(msg.timestamp);
      groups[dateLabel] ??= [];
      groups[dateLabel].push(msg);
      return groups;
    }, {});
  }, [messages]);

  return (
    <div className="flex h-screen w-full">
      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top Bar */}
        <div className="border-slate-200 flex items-center gap-2 border-b px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-sans text-xl font-semibold text-[#6A697D]">
              {activeLabel}
            </span>
            <span className="font-sans text-sm font-semibold text-[#6A697D]">
              • {messages?.length} messages
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              className="hover:bg-slate-100 rounded p-2 font-bold text-[#6A697D]"
              onClick={() => setShowRight(s => !s)}
              title="Toggle thread panel"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Messages Section */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl py-2">
            {Object.entries(groupedMessages || {}).map(([dateLabel, msgs]) => (
              <div key={dateLabel}>
                {/* Date Header */}
                <div className="sticky top-0 z-10 mx-4 my-2 text-center">
                  <span className="border-slate-200 inline-flex items-center rounded-full border bg-white px-3 py-1 font-sans text-xs font-medium text-[#6A697D] shadow-sm">
                    {dateLabel}
                  </span>
                </div>

                {/* Render messages */}
                {msgs.map(m => {
                  const user =
                    usersData?.members.find(u => u.user_id === m.sender_id) ??
                    usersData?.members[1];
                  return (
                    <div
                      key={m.id}
                      className="hover:bg-slate-50 mt-3 flex items-center gap-3 rounded-[16px] border border-[#EDEDFD] bg-white px-6 py-6 shadow-[0_2px_16px_0_rgba(10,10,105,0.10)]"
                    >
                      <Avatar user={user} size="12" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="font-sans text-[18px] font-semibold text-[#2A2942]">
                            {user?.full_name ?? "-"}
                          </span>
                          <span className="font-sans text-[14px] font-medium text-[#6A697D]">
                            {formatMessageTime(m.timestamp)}
                          </span>
                        </div>
                        <div className="mt-1 whitespace-pre-wrap break-words font-sans text-[14px] font-medium leading-relaxed text-[#6A697D]">
                          {htmlToText(m.content)}
                        </div>

                        {m.reactions?.length ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {m.reactions.map(r => (
                              <button
                                key={`${r.emoji}-${r.count}`}
                                className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full border px-2 py-0.5 text-xs"
                              >
                                {r.emoji} {r.count}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Composer */}
        <Composer
          dmId={dmId}
          channelId={channelId}
          isDM={isDM}
          refetchDMChatData={refetchDMChatData}
          refetchChannelChatData={refetchChannelChatData}
        />
      </div>

      {/* Right Sidebar */}
      {showRight && (
        <div className="border-slate-200 w-80 border-l md:block">
          <div className="flex h-full flex-col">
            <div className="border-slate-200 flex items-center justify-between border-b px-3 py-2">
              <div className="font-sans text-[18px] font-semibold text-[#6A697D]">
                Details
              </div>
              <button
                className="hover:bg-slate-100 rounded p-2 font-bold text-[#6A697D]"
                onClick={() => setShowRight(false)}
              >
                ✕
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-3 text-sm">
              {channelUserList.length > 0 && !isDM && (
                <div>
                  <div className="font-sans text-sm font-medium uppercase text-[#6A697D]">
                    Members
                  </div>

                  <ul className="mt-2 space-y-3">
                    {channelUserList?.map(u => (
                      <li
                        key={u.user_id}
                        className="py-2)] flex items-center gap-3 px-4"
                      >
                        <Avatar user={u} size="12" />
                        <div className="leading-tight">
                          <div className="font-sans text-[14px] font-semibold text-[#3F3C8D]">
                            {u.full_name}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {/* <div className="border-t border-slate-200 p-3 text-xs text-slate-500">
              Zulip
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
